import React, { useEffect, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Slider from '@material-ui/core/Slider';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter';
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft';
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import PublishIcon from '@material-ui/icons/Publish';
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom';
import VerticalAlignCenterIcon from '@material-ui/icons/VerticalAlignCenter';
import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop';
import { makeStyles } from '@material-ui/core/styles';

import { CANVAS_BOX, computeAlignment } from '../alignment';
import {
  fetchStarter,
  imagePreviewUrl,
  listFonts,
  listImages,
  measureImage,
  uploadImage,
} from '../imagesApi';
import useRenderedImage from '../hooks/useRenderedImage';
import { colors } from '../theme';
import { blockSign, nonNegativeChange } from '../../Root/nonNegative';

const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  toolbarButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  canvas: {
    position: 'relative',
    width: '100%',
    maxWidth: 520,
    margin: '0 auto',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#fff',
    userSelect: 'none',
  },
  canvasImage: {
    display: 'block',
    width: '100%',
    pointerEvents: 'none',
  },
  handle: {
    position: 'absolute',
    width: 16,
    height: 16,
    marginLeft: -8,
    marginTop: -8,
    borderRadius: '50%',
    border: '2px solid #fff',
    backgroundColor: colors.accent,
    boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
    cursor: 'grab',
    color: '#fff',
    fontSize: 9,
    lineHeight: '12px',
    textAlign: 'center',
    fontWeight: 700,
  },
  handleActive: {
    backgroundColor: '#e53935',
    zIndex: 2,
  },
  hint: {
    display: 'block',
    textAlign: 'center',
    color: colors.textDim,
    marginTop: theme.spacing(1),
  },
  list: {
    marginTop: theme.spacing(2),
  },
  row: {
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: colors.panelAlt },
  },
  rowActive: {
    backgroundColor: colors.accentSoft,
    fontWeight: 600,
    '&:hover': { backgroundColor: colors.accentSoft },
  },
  editor: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: colors.panelAlt,
  },
  line: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  sliderLabel: {
    fontSize: 12,
    color: colors.textDim,
    marginTop: theme.spacing(1.5),
  },
  chips: {
    marginTop: theme.spacing(1),
  },
  chip: {
    marginRight: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
  error: {
    color: '#d32f2f',
    marginTop: theme.spacing(1),
  },
  library: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5),
  },
  thumb: {
    width: 64,
    height: 92,
    objectFit: 'cover',
    borderRadius: 4,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `2px solid transparent`,
    cursor: 'pointer',
  },
  thumbActive: {
    border: `2px solid ${colors.accent}`,
  },
}));

const clamp01 = (n) => Math.min(1, Math.max(0, n));
const round = (n) => Math.round(n * 10000) / 10000;

const newField = (index) => ({
  id: `f${Date.now()}${index}`,
  text: 'Новый текст',
  x: 0.1,
  y: 0.1 + index * 0.04,
  size: 0.04,
  color: '#000000',
  font: 'calibri',
  bold: false,
  align: 'left',
  width: 0,
  lineHeight: 1.2,
});

// Редактор картинки для заголовка шаблона. Фон (без текста) загружается один раз,
// а весь текст на нём это поля: их можно двигать, менять и привязывать к
// переменным шаблона ({{2}} подставится значением из рассылки).
//   image = { file, fields }; variables = [{ key, label }]; values = { key: пример }
//   onStarter(template) — вызывается, когда выбрана готовая афиша: к ней есть заготовка текста
function TemplateImageEditor(props) {
  const classes = useStyles();
  const { image, onChange, variables, values, onPreview, onStarter } = props;
  const canvasRef = useRef(null);
  const textRef = useRef(null);
  const fileInput = useRef(null);

  const [selected, setSelected] = useState('');
  const [library, setLibrary] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [alignRef, setAlignRef] = useState('canvas');
  const [fonts, setFonts] = useState([{ id: 'carlito', label: 'Carlito (как Calibri)' }]);

  const { fields } = image;
  const current = fields.find((field) => field.id === selected) || null;

  const rendered = useRenderedImage(
    image.file ? { file: image.file, config: { fields }, values } : null
  );

  useEffect(() => {
    if (!selected && fields.length > 0) setSelected(fields[0].id);
  }, [fields, selected]);

  useEffect(() => {
    listFonts().then((result) => {
      if (result.ok && result.data.length > 0) setFonts(result.data);
    });
  }, []);

  useEffect(() => {
    if (onPreview) onPreview(rendered.url);
  }, [rendered.url]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFields = (next) => onChange({ ...image, fields: next });
  const updateField = (id, patch) =>
    setFields(fields.map((field) => (field.id === id ? { ...field, ...patch } : field)));

  const applyFile = (file, layout) => {
    setMessage('');
    onChange({ file, fields: layout || fields });
    setSelected(layout && layout.length ? layout[0].id : selected);
  };

  const handleUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setMessage('Нужен файл PNG или JPG.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setMessage('Файл больше 4 МБ. Сожмите картинку и загрузите снова.');
      return;
    }
    setBusy(true);
    try {
      const result = await uploadImage(file);
      if (!result.ok) {
        setMessage(result.error);
      } else {
        applyFile(result.data.file);
        setLibrary([]);
      }
    } catch (err) {
      setMessage(err.message);
    }
    setBusy(false);
  };

  const handleStarter = async () => {
    setBusy(true);
    const result = await fetchStarter();
    setBusy(false);
    if (!result.ok) {
      setMessage(result.error);
      return;
    }
    applyFile(result.data.file, result.data.layout.fields);
    if (onStarter) onStarter(result.data.template);
  };

  const handleLibrary = async () => {
    if (!showLibrary && library.length === 0) {
      const result = await listImages();
      if (result.ok) setLibrary(result.data);
    }
    setShowLibrary(!showLibrary);
  };

  const startDrag = (event, id) => {
    event.preventDefault();
    setSelected(id);
    const move = (moveEvent) => {
      const rect = canvasRef.current.getBoundingClientRect();
      updateField(id, {
        x: round(clamp01((moveEvent.clientX - rect.left) / rect.width)),
        y: round(clamp01((moveEvent.clientY - rect.top) / rect.height)),
      });
    };
    const stop = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
  };

  // Опора выравнивания: края картинки или другой текст (если он ещё есть).
  const refId = alignRef !== 'canvas' && fields.some((f) => f.id === alignRef && f.id !== selected) ? alignRef : 'canvas';

  const alignTo = async (op) => {
    if (!current) return;
    setBusy(true);
    const result = await measureImage(image.file, fields, values);
    setBusy(false);
    if (!result.ok) {
      setMessage(result.error);
      return;
    }
    const own = result.data.find((box) => box.id === current.id);
    const ref = refId === 'canvas' ? CANVAS_BOX : result.data.find((box) => box.id === refId);
    if (!own || !ref) return;
    setMessage('');
    updateField(current.id, computeAlignment(current, own, ref, op));
  };

  const insertVariable = (key) => {
    if (!current) return;
    const el = textRef.current;
    const start = el ? el.selectionStart : current.text.length;
    const end = el ? el.selectionEnd : current.text.length;
    const token = `{{${key}}}`;
    updateField(current.id, { text: current.text.slice(0, start) + token + current.text.slice(end) });
  };

  const addField = () => {
    const field = newField(fields.length);
    setFields([...fields, field]);
    setSelected(field.id);
  };

  const removeField = (id) => {
    const rest = fields.filter((field) => field.id !== id);
    setFields(rest);
    setSelected(rest.length ? rest[0].id : '');
  };

  return (
    <div>
      <div className={classes.toolbar}>
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
        <Button
          variant="outlined"
          size="small"
          className={classes.toolbarButton}
          startIcon={<PublishIcon />}
          disabled={busy}
          onClick={() => fileInput.current.click()}
        >
          Загрузить фон
        </Button>
        <Button
          variant="outlined"
          size="small"
          className={classes.toolbarButton}
          startIcon={<PhotoLibraryIcon />}
          disabled={busy}
          onClick={handleLibrary}
        >
          Загруженные фоны
        </Button>
        <Button
          variant="outlined"
          size="small"
          className={classes.toolbarButton}
          disabled={busy}
          onClick={handleStarter}
        >
          Готовый пример (закупка соняшника)
        </Button>
      </div>

      {showLibrary && (
        <div className={classes.library}>
          {library.length === 0 && (
            <Typography variant="caption" color="textSecondary">
              Загруженных фонов пока нет.
            </Typography>
          )}
          {library.map((item) => (
            <img
              key={item.file}
              src={imagePreviewUrl(item.file)}
              alt=""
              crossOrigin="use-credentials"
              className={`${classes.thumb} ${item.file === image.file ? classes.thumbActive : ''}`}
              onClick={() => applyFile(item.file)}
            />
          ))}
        </div>
      )}

      {message && (
        <Typography variant="body2" className={classes.error}>
          {message}
        </Typography>
      )}

      {!image.file && (
        <Typography variant="body2" color="textSecondary">
          Загрузите фон: картинку без текста (логотип, фото). Весь текст вы добавите
          поверх неё и сможете менять в любой момент. PNG или JPG до 4 МБ.
        </Typography>
      )}

      {image.file && (
        <>
          <div className={classes.canvas} ref={canvasRef}>
            {rendered.url ? (
              <img src={rendered.url} alt="" className={classes.canvasImage} />
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: colors.textDim }}>
                Строим картинку…
              </div>
            )}
            {fields.map((field, index) => (
              <div
                key={field.id}
                role="button"
                tabIndex={0}
                title={field.text.slice(0, 40)}
                className={`${classes.handle} ${field.id === selected ? classes.handleActive : ''}`}
                style={{ left: `${field.x * 100}%`, top: `${field.y * 100}%` }}
                onMouseDown={(event) => startDrag(event, field.id)}
              >
                {index + 1}
              </div>
            ))}
          </div>
          <Typography variant="caption" className={classes.hint}>
            Круглые метки показывают, где начинается каждый текст. Перетащите метку,
            чтобы передвинуть текст.
          </Typography>
          {rendered.error && (
            <Typography variant="body2" className={classes.error}>
              {rendered.error}
            </Typography>
          )}

          <div className={classes.list}>
            {fields.map((field, index) => (
              <div
                key={field.id}
                role="button"
                tabIndex={0}
                className={`${classes.row} ${field.id === selected ? classes.rowActive : ''}`}
                onClick={() => setSelected(field.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') setSelected(field.id);
                }}
              >
                {index + 1}. {field.text.replace(/\n/g, ' ') || '(пусто)'}
              </div>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addField} style={{ marginTop: 8 }}>
              Добавить текст
            </Button>
          </div>

          {current && (
            <div className={classes.editor}>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                inputProps={{ style: { resize: 'vertical' } }}
                label="Текст (можно несколько строк)"
                value={current.text}
                inputRef={textRef}
                onChange={(event) => updateField(current.id, { text: event.target.value })}
              />
              {variables.length > 0 && (
                <div className={classes.chips}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Вставить переменную из шаблона (подставится значение из рассылки):
                  </Typography>
                  {variables.map((variable) => (
                    <Chip
                      key={variable.key}
                      size="small"
                      className={classes.chip}
                      label={`{{${variable.key}}} ${variable.label || ''}`.trim()}
                      onClick={() => insertVariable(variable.key)}
                    />
                  ))}
                </div>
              )}

              <div className={classes.line} style={{ marginTop: 16 }}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="Шрифт"
                  value={fonts.some((font) => font.id === current.font) ? current.font : 'carlito'}
                  style={{ minWidth: 260, marginRight: 12 }}
                  onChange={(event) => updateField(current.id, { font: event.target.value })}
                >
                  {fonts.map((font) => (
                    <MenuItem key={font.id} value={font.id}>
                      {font.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  size="small"
                  disabled={fields.length < 2}
                  onClick={() =>
                    setFields(fields.map((field) => ({ ...field, font: current.font || 'calibri' })))
                  }
                >
                  Этот шрифт для всех текстов
                </Button>
              </div>

              <Typography className={classes.sliderLabel}>
                Размер шрифта: {(current.size * 100).toFixed(1)}% ширины картинки
              </Typography>
              <Slider
                min={1}
                max={15}
                step={0.1}
                value={current.size * 100}
                onChange={(event, value) => updateField(current.id, { size: round(value / 100) })}
              />

              <Typography className={classes.sliderLabel}>
                Перенос длинных строк:{' '}
                {current.width > 0 ? `на ${Math.round(current.width * 100)}% ширины` : 'не переносить'}
              </Typography>
              <Slider
                min={0}
                max={100}
                step={1}
                value={current.width * 100}
                onChange={(event, value) => updateField(current.id, { width: round(value / 100) })}
              />

              <div className={classes.line}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="Привязка текста к метке"
                  value={current.align}
                  style={{ minWidth: 210, marginRight: 16 }}
                  onChange={(event) => updateField(current.id, { align: event.target.value })}
                >
                  <MenuItem value="left">От метки вправо</MenuItem>
                  <MenuItem value="center">По центру метки</MenuItem>
                  <MenuItem value="right">От метки влево</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  variant="outlined"
                  type="number"
                  label="Межстрочный интервал"
                  value={current.lineHeight}
                  inputProps={{ min: 0.8, max: 3, step: 0.05, onKeyDown: blockSign }}
                  style={{ width: 190, marginRight: 16 }}
                  onChange={nonNegativeChange((event) =>
                    updateField(current.id, { lineHeight: Number(event.target.value) || 1.2 })
                  )}
                />
                <label style={{ marginRight: 16 }}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Цвет
                  </Typography>
                  <input
                    type="color"
                    value={current.color}
                    onChange={(event) => updateField(current.id, { color: event.target.value })}
                  />
                </label>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={current.bold}
                      onChange={(event) => updateField(current.id, { bold: event.target.checked })}
                    />
                  }
                  label="Жирный"
                />
              </div>

              <div className={classes.line}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={Boolean(current.bullet)}
                      onChange={(event) => updateField(current.id, { bullet: event.target.checked })}
                    />
                  }
                  label="Маркированный список (квадрат перед каждой строкой)"
                />
              </div>
              {current.bullet && (
                <div className={classes.line}>
                  <label style={{ marginRight: 16 }}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Цвет маркера
                    </Typography>
                    <input
                      type="color"
                      value={current.bulletColor || current.color}
                      onChange={(event) =>
                        updateField(current.id, { bulletColor: event.target.value })
                      }
                    />
                  </label>
                  <TextField
                    size="small"
                    variant="outlined"
                    type="number"
                    label="Отступ текста от маркера, %"
                    value={round((current.indent === undefined ? 0.04 : current.indent) * 100)}
                    inputProps={{ min: 0.5, max: 20, step: 0.1, onKeyDown: blockSign }}
                    style={{ width: 230 }}
                    onChange={nonNegativeChange((event) =>
                      updateField(current.id, { indent: round(Number(event.target.value) / 100) })
                    )}
                  />
                </div>
              )}

              <div className={classes.line} style={{ marginTop: 8 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  type="number"
                  label="Слева, % ширины"
                  value={round(current.x * 100)}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  style={{ width: 160, marginRight: 16 }}
                  onChange={(event) =>
                    updateField(current.id, { x: clamp01(Number(event.target.value) / 100) })
                  }
                />
                <TextField
                  size="small"
                  variant="outlined"
                  type="number"
                  label="Сверху, % высоты"
                  value={round(current.y * 100)}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  style={{ width: 160 }}
                  onChange={(event) =>
                    updateField(current.id, { y: clamp01(Number(event.target.value) / 100) })
                  }
                />
              </div>

              <Typography className={classes.sliderLabel} style={{ marginTop: 16 }}>
                Выровнять этот текст
              </Typography>
              <div className={classes.line}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="Относительно"
                  value={refId}
                  style={{ minWidth: 260 }}
                  onChange={(event) => setAlignRef(event.target.value)}
                >
                  <MenuItem value="canvas">Краёв картинки (отступ 5%)</MenuItem>
                  {fields.map((field, index) =>
                    field.id === current.id ? null : (
                      <MenuItem key={field.id} value={field.id}>
                        {`Текста ${index + 1}: ${field.text.replace(/\n/g, ' ').slice(0, 28)}`}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </div>
              <div className={classes.line}>
                <ButtonGroup size="small" disabled={busy} style={{ marginRight: 16, marginBottom: 8 }}>
                  <Button startIcon={<FormatAlignLeftIcon />} onClick={() => alignTo('left')}>
                    По левому
                  </Button>
                  <Button startIcon={<FormatAlignCenterIcon />} onClick={() => alignTo('hcenter')}>
                    По центру
                  </Button>
                  <Button startIcon={<FormatAlignRightIcon />} onClick={() => alignTo('right')}>
                    По правому
                  </Button>
                </ButtonGroup>
                <ButtonGroup size="small" disabled={busy} style={{ marginBottom: 8 }}>
                  <Button startIcon={<VerticalAlignTopIcon />} onClick={() => alignTo('top')}>
                    По верху
                  </Button>
                  <Button startIcon={<VerticalAlignCenterIcon />} onClick={() => alignTo('vmiddle')}>
                    По середине
                  </Button>
                  <Button startIcon={<VerticalAlignBottomIcon />} onClick={() => alignTo('bottom')}>
                    По низу
                  </Button>
                </ButtonGroup>
              </div>

              <Button
                size="small"
                color="secondary"
                startIcon={<DeleteIcon />}
                style={{ marginTop: 12 }}
                onClick={() => removeField(current.id)}
              >
                Удалить этот текст
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TemplateImageEditor;
